import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  Button,
  CopyLinkButton,
  EmptyState,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Modal,
  Pager,
  cx,
  useCursorPager,
  usePrefetchNext,
  useToast,
} from "@/components/ui";
import { DataFreshness, freshnessOf } from "@/components/ui/DataFreshness";
import { portal } from "@/lib/api";
import { civilDay, formatInZone } from "@/lib/format";
import type {
  NotificationCategory,
  NotificationFilter,
  NotificationIcon,
  NotificationInbox,
  PortalNotification,
} from "@/lib/types";
import { choice, count, useUrlView } from "@/lib/url-state";

import { previewBulkDismiss, type BulkDismissPreview } from "./bulk";

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  billing: "Billing",
  domains: "Domains",
  website: "Website",
  hosting: "Hosting",
};
/** The filter chips name the website category after Weavex, which builds and publishes it. */
const CHIP_LABEL: Record<NotificationCategory, string> = { ...CATEGORY_LABEL, website: "Weavex" };
const ICON_FILE: Record<NotificationIcon, string> = {
  card: "card",
  invoice: "invoice",
  weavex: "weavex",
  browser: "browser",
  globe: "globe",
  lock: "lock",
  server: "browser",
};
const PAGE_DAYS = 7;
const MORE_DAYS = 30;

type Filter = NotificationFilter;

/** The longest window "Load older" reaches from a link. */
const MAX_DAYS = 400;

/**
 * The view kept in the URL (PHP-3341): `?filter=` (default all), `?sort=` (default newest first,
 * grouped by day) and `?days=` (default the last 7 days; "Load older" adds 30).
 */
const VIEW = {
  filter: choice(
    "filter",
    ["all", "action_required", "unread", "billing", "domains", "website", "hosting"] as const,
    "all",
  ),
  sort: choice("sort", ["newest", "oldest", "action"] as const, "newest"),
  days: count("days", PAGE_DAYS, PAGE_DAYS, MAX_DAYS),
};

/** A Figma glyph (public/icons/notifications), centred at its drawn size in a `box`px slot. */
function Glyph({ name, box, className }: { name: string; box: number; className?: string }) {
  return (
    <span
      className={cx("inline-flex shrink-0 items-center justify-center", className)}
      style={{ width: box, height: box }}
    >
      <img src={`/icons/notifications/${name}.svg`} alt="" className="block max-w-none" />
    </span>
  );
}

// ── time ────────────────────────────────────────────────────────────────────
// Days and times are read in the portal's display time zone (lib/time-zone.ts); the wording stays en-GB.
const EN_GB = "en-GB";
const hhmm = (iso: string) => formatInZone(iso, { hour: "2-digit", minute: "2-digit" }, EN_GB);
const on = (iso: string, options: Intl.DateTimeFormatOptions) => formatInZone(iso, options, EN_GB);

function daysAgo(iso: string, now: Date): number {
  return civilDay(now) - civilDay(iso);
}

function groupLabel(iso: string, now: Date): string {
  const ago = daysAgo(iso, now);
  const date = on(iso, { weekday: "long", day: "numeric", month: "long" });
  if (ago <= 0) return `Today · ${date}`;
  if (ago === 1) return `Yesterday · ${date}`;
  if (ago < 7) return "Earlier this week";
  return on(iso, { month: "long", year: "numeric" });
}

function relative(iso: string, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  const ago = daysAgo(iso, now);
  if (ago <= 0) {
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  }
  if (ago === 1) return "yesterday";
  if (ago < 7) return on(iso, { weekday: "long" });
  return on(iso, { day: "numeric", month: "short" });
}

function stamp(iso: string, now: Date): string {
  const ago = daysAgo(iso, now);
  if (ago <= 1) return hhmm(iso);
  if (ago < 7) return `${on(iso, { weekday: "short" })} ${hhmm(iso)}`;
  return `${on(iso, { day: "numeric", month: "short" })} ${hhmm(iso)}`;
}

// ── page ────────────────────────────────────────────────────────────────────
/** Figma "Notifications · dense list". */
export function NotificationsPage() {
  const { orgId, current, can } = useOrganization();
  const qc = useQueryClient();
  const [{ filter, sort, days }, setView] = useUrlView(VIEW);
  const setFilter = (f: typeof filter) => setView({ filter: f });
  const setSort = (s: typeof sort) => setView({ sort: s });
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Captured once, when "Dismiss read notifications" is clicked, so the count stays right even if
  // `shown` changes while the confirm dialog is open (PHP-3355).
  const [confirmDismiss, setConfirmDismiss] = useState<BulkDismissPreview | null>(null);
  const toast = useToast();
  // The server filters, orders and pages the list; a new window, filter or order starts again at the first page.
  const pager = useCursorPager(`${days}|${filter}|${sort}`);
  const pageKey = (token: string | null) => [
    "notifications",
    orgId,
    "page",
    { days, filter, sort, size: pager.pageSize, token },
  ];
  const fetchPage = (token: string | null) =>
    portal.notifications(orgId, days, { filter, sort, pageSize: pager.pageSize, pageToken: token });
  const key = pageKey(pager.token);
  const inbox = useQuery({
    queryKey: key,
    queryFn: () => fetchPage(pager.token),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
  usePrefetchNext(inbox.data?.next_page_token, pageKey, fetchPage);
  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications", orgId] });
  const mark = useMutation({
    mutationFn: (body: { keys: string[]; read?: boolean; dismissed?: boolean }) =>
      portal.markNotifications(orgId, body),
    // Show the change straight away on this page and in the counts; the refetch confirms it.
    onMutate: ({ keys, read, dismissed }) => {
      qc.setQueryData<NotificationInbox>(key, (old) => old && applyMarks(old, filter, keys, read, dismissed));
    },
    onSettled: refresh,
  });
  const readAll = useMutation({ mutationFn: () => portal.readAllNotifications(orgId), onSettled: refresh });

  // Dismissing is the only bulk action that can't be put back by just clicking it again, so it asks
  // first (with the count and scope captured at click time) and offers Undo afterwards (PHP-3355).
  const confirmBulkDismiss = () => {
    if (!confirmDismiss) return;
    const { keys, count: dismissedCount } = confirmDismiss;
    setConfirmDismiss(null);
    mark.mutate(
      { keys, dismissed: true },
      {
        onSuccess: () =>
          toast.show({
            tone: "success",
            title: `Dismissed ${dismissedCount} notification${dismissedCount === 1 ? "" : "s"}`,
            action: { label: "Undo", onClick: () => mark.mutate({ keys, dismissed: false }) },
          }),
      },
    );
  };

  // "Now" is when the list was fetched, so labels like "2h ago" match the data on screen.
  const now = new Date(inbox.dataUpdatedAt);
  const shown = useMemo(() => inbox.data?.notifications ?? [], [inbox.data]);

  if (inbox.isPending) return <FullPageSpinner />;
  if (inbox.isError) return <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />;
  const data = inbox.data;
  // Counts describe the whole window, whatever the filter or page.
  const count = (k: string) => data.counts[k] ?? 0;
  const unread = count("unread");
  const categories = (["billing", "domains", "website", "hosting"] as const).filter(
    (c) =>
      !data.muted_categories.includes(c) &&
      (c !== "billing" || can("billing:read")) &&
      (c !== "hosting" || count(c) > 0),
  );

  // Group rows under day headings (only when sorted by time).
  const groups: { label: string; items: PortalNotification[] }[] = [];
  if (sort === "action") groups.push({ label: "Action required first", items: shown });
  else
    for (const n of shown) {
      const label = groupLabel(n.occurred_at, now);
      const last = groups.at(-1);
      if (last?.label === label) last.items.push(n);
      else groups.push({ label, items: [n] });
    }

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-wrap items-center gap-3.5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted">
          <Glyph name="bell-head" box={20} />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <h1 className="text-[30px] leading-9 font-semibold text-fg">Notifications</h1>
            {unread > 0 && (
              <span className="flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                <img src="/icons/notifications/dot.svg" alt="" className="size-1.5" />
                {unread} unread
              </span>
            )}
          </div>
          <p className="text-sm text-[#766e6a]">
            Everything that needs your attention on {current.organization.name}
          </p>
          <DataFreshness {...freshnessOf(inbox)} />
        </div>
        <span className="flex-1" />
        <Button
          unstyled
          className={toolButton}
          loading={readAll.isPending}
          disabledReason={unread === 0 ? "Everything is already read." : undefined}
          onClick={() => readAll.mutate()}
        >
          <Glyph name="check-btn" box={15} />
          Mark all as read
        </Button>
        <Button
          unstyled
          type="button"
          className="inline-flex items-center gap-2 rounded-[10px] bg-chrome-raised px-4 py-[11px] text-sm font-semibold text-white hover:bg-black"
          onClick={() => setPrefsOpen(true)}
        >
          <Glyph name="settings" box={15} />
          Preferences
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Chip active={filter === "all"} count={count("all")} onClick={() => setFilter("all")}>
          All
        </Chip>
        <Chip
          active={filter === "action_required"}
          count={count("action_required")}
          onClick={() => setFilter("action_required")}
        >
          Action required
        </Chip>
        <Chip active={filter === "unread"} count={unread} onClick={() => setFilter("unread")}>
          Unread
        </Chip>
        {categories.map((c) => (
          <Chip key={c} active={filter === c} count={count(c)} onClick={() => setFilter(c)}>
            {CHIP_LABEL[c]}
          </Chip>
        ))}
        <span className="flex-1" />
        <CopyLinkButton className="py-[11px] text-sm" />
        <Menu
          label="Sort"
          icon="arrow-down"
          items={[
            { label: "Newest first", checked: sort === "newest", onSelect: () => setSort("newest") },
            { label: "Oldest first", checked: sort === "oldest", onSelect: () => setSort("oldest") },
            { label: "Action required first", checked: sort === "action", onSelect: () => setSort("action") },
          ]}
        />
        <Menu
          label="Bulk"
          icon="more"
          items={[
            {
              label: "Mark shown as read",
              disabledReason: shown.some((n) => !n.read) ? undefined : "Everything shown is already read.",
              onSelect: () =>
                mark.mutate({ keys: shown.filter((n) => !n.read).map((n) => n.key), read: true }),
            },
            {
              label: "Mark shown as unread",
              disabledReason: shown.some((n) => n.read) ? undefined : "Nothing shown has been read yet.",
              onSelect: () =>
                mark.mutate({ keys: shown.filter((n) => n.read).map((n) => n.key), read: false }),
            },
            {
              label: "Dismiss read notifications",
              disabledReason: shown.some((n) => n.read) ? undefined : "Nothing shown has been read yet.",
              // Snapshot `shown` now: `previewBulkDismiss` captures the keys the dialog confirms against,
              // so a change to the list while it's open can't alter what gets dismissed.
              onSelect: () => setConfirmDismiss(previewBulkDismiss(shown, filter)),
            },
          ]}
        />
      </div>

      {(mark.error ?? readAll.error) && <ErrorAlert error={mark.error ?? readAll.error} />}

      <div className="overflow-hidden rounded-[14px] border border-border bg-surface">
        {data.total === 0 ? (
          <EmptyState
            icon={<Glyph name="bell-head" box={20} />}
            title={filter === "all" ? "You’re all caught up" : "Nothing here"}
            description={
              filter === "all"
                ? `No notifications in the last ${data.window_days} days.`
                : "No notifications match this filter."
            }
            action={
              filter !== "all" && (
                <Button variant="secondary" size="sm" onClick={() => setFilter("all")}>
                  Clear filters
                </Button>
              )
            }
          />
        ) : (
          groups.map((g) => (
            <section key={g.label}>
              <div className="flex items-center gap-2.5 bg-[#faf6ef] px-[22px] py-[11px]">
                <h2 className="text-xs font-semibold text-[#766e6a] uppercase">{g.label}</h2>
                <span className="flex-1" />
                <span className="text-xs font-medium text-fg-subtle">{g.items.length}</span>
              </div>
              {g.items.map((n) => (
                <Row
                  key={n.key}
                  n={n}
                  now={now}
                  onRead={(read) => mark.mutate({ keys: [n.key], read })}
                  onDismiss={() => mark.mutate({ keys: [n.key], dismissed: true })}
                />
              ))}
            </section>
          ))
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-[#faf6ef] px-[22px] py-4">
          <Pager
            pager={pager}
            shown={shown.length}
            total={data.total}
            hasNext={!!data.next_page_token}
            onNext={() => data.next_page_token && pager.next(data.next_page_token)}
            loading={inbox.isFetching}
            noun="notifications"
            className="min-w-0 flex-1"
          />
          <p className="text-xs text-[#766e6a]">From the last {data.window_days} days</p>
          {data.has_older && (
            <Button
              unstyled
              type="button"
              className={rowButton}
              disabled={inbox.isFetching}
              aria-busy={inbox.isFetching}
              // More of the same list, not a new view: Back shouldn't just shorten it again.
              onClick={() => setView({ days: Math.min(days + MORE_DAYS, MAX_DAYS) }, { replace: true })}
            >
              {inbox.isFetching ? "Loading…" : "Load older"}
            </Button>
          )}
        </div>
      </div>

      {prefsOpen && (
        <PreferencesModal
          open
          onClose={() => setPrefsOpen(false)}
          muted={data.muted_categories}
          canBilling={can("billing:read")}
          onSaved={refresh}
        />
      )}

      {confirmDismiss && (
        <Modal
          open
          onClose={() => setConfirmDismiss(null)}
          title={confirmDismiss.title}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmDismiss(null)}>
                Cancel
              </Button>
              <Button loading={mark.isPending} onClick={confirmBulkDismiss}>
                Dismiss
              </Button>
            </>
          }
        >
          <p className="text-sm text-fg-muted">{confirmDismiss.scope}</p>
        </Modal>
      )}
    </div>
  );
}

function matches(n: PortalNotification, filter: Filter): boolean {
  if (filter === "all") return true;
  if (filter === "unread") return !n.read;
  if (filter === "action_required") return n.severity === "action_required";
  return n.category === filter;
}

/**
 * The cached page after the person read or dismissed some notifications: the rows change, the window's counts
 * follow, and a row that no longer belongs under the filter (a read one under "Unread") leaves the page and the
 * total. The server's answer replaces this as soon as it arrives.
 */
function applyMarks(
  old: NotificationInbox,
  filter: Filter,
  keys: string[],
  read: boolean | undefined,
  dismissed: boolean | undefined,
): NotificationInbox {
  const counts = { ...old.counts };
  const bump = (k: string, by: number) => (counts[k] = Math.max(0, (counts[k] ?? 0) + by));
  let total = old.total;
  const notifications: PortalNotification[] = [];
  for (const n of old.notifications) {
    if (!keys.includes(n.key)) {
      notifications.push(n);
      continue;
    }
    if (dismissed) {
      bump("all", -1);
      bump(n.category, -1);
      if (!n.read) bump("unread", -1);
      if (n.severity === "action_required") bump("action_required", -1);
      total -= 1;
      continue;
    }
    const next = read === undefined ? n : { ...n, read };
    if (next.read !== n.read) bump("unread", next.read ? -1 : 1);
    if (matches(next, filter)) notifications.push(next);
    else total -= 1;
  }
  return { ...old, notifications, counts, total: Math.max(0, total) };
}

const toolButton =
  "inline-flex items-center gap-2 rounded-[10px] border border-border bg-surface px-4 py-[11px] text-sm font-medium text-[#443e3b] hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60";
const rowButton =
  "inline-flex shrink-0 items-center rounded-[9px] border border-border bg-surface px-3.5 py-[9px] text-xs font-semibold whitespace-nowrap text-[#443e3b] hover:bg-muted disabled:opacity-60";
const rowPrimary =
  "inline-flex shrink-0 items-center rounded-[9px] bg-brand-500 px-3.5 py-[9px] text-xs font-semibold whitespace-nowrap text-white hover:bg-brand-600";

function Chip({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      unstyled
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-2 rounded-full py-[9px] pr-2 pl-3.5 text-sm font-medium",
        active
          ? "bg-chrome-raised text-white"
          : "border border-border bg-surface text-fg-muted hover:bg-muted",
      )}
    >
      {children}
      <span
        className={cx(
          "rounded-full px-[7px] py-0.5 text-xs font-semibold",
          active ? "bg-fg-muted text-white" : "bg-muted text-[#766e6a]",
        )}
      >
        {count}
      </span>
    </Button>
  );
}

function Menu({
  label,
  icon,
  items,
}: {
  label: string;
  icon: string;
  items: { label: string; checked?: boolean; disabledReason?: string; onSelect: () => void }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <Button
        unstyled
        type="button"
        className={toolButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Glyph name={icon} box={15} />
        {label}
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1.5 w-56 rounded-xl border border-border bg-surface p-1.5 shadow-lg"
        >
          {items.map((item) => (
            <Button
              unstyled
              key={item.label}
              role="menuitem"
              disabledReason={item.disabledReason}
              onClick={() => {
                item.onSelect();
                setOpen(false);
              }}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-fg hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              {item.label}
              {item.checked && <img src="/icons/check.svg" alt="" className="size-3.5" />}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

const TILE_BG = { alert: "bg-[#fff1ee]", pending: "bg-[#fef4e5]" } as const;
const ACTION_PILL = { alert: "bg-[#fff1ee] text-[#801b18]", pending: "bg-[#fef4e5] text-[#703600]" } as const;
const BADGE_ICON = { alert: "alert", pending: "clock-pending", done: "done" } as const;

function Row({
  n,
  now,
  onRead,
  onDismiss,
}: {
  n: PortalNotification;
  now: Date;
  onRead: (read: boolean) => void;
  onDismiss: () => void;
}) {
  const tone = n.severity === "action_required" ? (n.badge === "pending" ? "pending" : "alert") : null;
  const tileBg = tone ? TILE_BG[tone] : n.icon === "weavex" ? "bg-brand-50" : "bg-muted";
  return (
    <article
      className={cx(
        "flex items-start gap-3.5 border-b border-muted px-[22px] py-4",
        n.read ? "bg-surface" : "bg-[#fefcfa]",
      )}
    >
      <div className="relative size-[38px] shrink-0">
        <div className={cx("flex size-9 items-center justify-center rounded-[11px]", tileBg)}>
          <Glyph name={ICON_FILE[n.icon]} box={17} />
        </div>
        {n.badge && (
          <span className="absolute top-6 left-6 flex size-4 items-center justify-center rounded-full bg-surface">
            <Glyph name={BADGE_ICON[n.badge]} box={12} />
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <h3 className="text-sm font-semibold text-fg">{n.title}</h3>
          <span className="text-xs text-fg-subtle">{relative(n.occurred_at, now)}</span>
          {tone && (
            <span className={cx("rounded-full px-2 py-0.5 text-xs font-semibold", ACTION_PILL[tone])}>
              Action required
            </span>
          )}
        </div>
        <p className="text-sm text-fg-muted">{n.body}</p>
        <p className="flex items-center gap-1.5 pt-0.5 text-xs text-fg-subtle">
          <Glyph name="clock-meta" box={12} />
          {stamp(n.occurred_at, now)} · {CATEGORY_LABEL[n.category]}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {n.action && <ActionButton action={n.action} onOpen={() => !n.read && onRead(true)} />}
        <Button
          unstyled
          type="button"
          className="rounded-lg p-[7px] hover:bg-muted"
          aria-label={n.read ? "Mark as unread" : "Mark as read"}
          title={n.read ? "Mark as unread" : "Mark as read"}
          onClick={() => onRead(!n.read)}
        >
          <Glyph name="bell" box={15} />
        </Button>
        <Button
          unstyled
          type="button"
          className="rounded-lg p-[7px] hover:bg-muted"
          aria-label="Dismiss"
          title="Dismiss"
          onClick={onDismiss}
        >
          <Glyph name="cancel" box={15} />
        </Button>
        {!n.read && <img src="/icons/notifications/unread.svg" alt="Unread" width={7} height={29} />}
      </div>
    </article>
  );
}

function ActionButton({
  action,
  onOpen,
}: {
  action: NonNullable<PortalNotification["action"]>;
  onOpen: () => void;
}) {
  const className = action.primary ? rowPrimary : rowButton;
  // Live sites and printable invoices open in a new tab; everything else stays in the portal.
  if (/^https?:\/\//.test(action.url) || action.url.startsWith("/billing/invoices/"))
    return (
      <a href={action.url} target="_blank" rel="noreferrer" className={className} onClick={onOpen}>
        {action.label}
      </a>
    );
  return (
    <Link to={action.url} className={className} onClick={onOpen}>
      {action.label}
    </Link>
  );
}

function PreferencesModal({
  open,
  onClose,
  muted,
  canBilling,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  muted: NotificationCategory[];
  canBilling: boolean;
  onSaved: () => void;
}) {
  const { orgId } = useOrganization();
  const [hidden, setHidden] = useState<NotificationCategory[]>(muted);
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => portal.setNotificationPreferences(orgId, hidden),
    onSuccess: () => {
      toast.show({ tone: "success", title: "Notification preferences saved" });
      onSaved();
      onClose();
    },
  });
  const rows: { c: NotificationCategory; hint: string }[] = [
    ...(canBilling
      ? [{ c: "billing" as const, hint: "Invoices, failed payments, cards and plan changes" }]
      : []),
    { c: "domains", hint: "Domains connecting, waiting on DNS or failing their check" },
    { c: "website", hint: "Weavex publishing your website and its new versions" },
    { c: "hosting", hint: "External sites deploying or failing to deploy" },
  ];
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Notification preferences"
      footer={
        <>
          <Button unstyled type="button" className={rowButton} onClick={onClose}>
            Cancel
          </Button>
          <Button
            unstyled
            type="button"
            className={rowPrimary}
            disabled={save.isPending}
            aria-busy={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save preferences"}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-fg-muted">
        Choose what shows in Notifications for you. Teammates keep their own settings. Email alerts are set in{" "}
        <Link to="/settings?tab=notifications" className="font-semibold text-fg underline" onClick={onClose}>
          Settings
        </Link>
        .
      </p>
      <div className="flex flex-col divide-y divide-border">
        {rows.map(({ c, hint }) => {
          const on = !hidden.includes(c);
          return (
            <label key={c} className="flex cursor-pointer items-center gap-3 py-3">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-fg">{CATEGORY_LABEL[c]}</span>
                <span className="block text-xs text-fg-subtle">{hint}</span>
              </span>
              <input
                type="checkbox"
                className="peer sr-only"
                checked={on}
                onChange={() => setHidden((h) => (on ? [...h, c] : h.filter((x) => x !== c)))}
              />
              <span
                aria-hidden
                className={cx(
                  "relative h-5 w-9 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500",
                  on ? "bg-brand-500" : "bg-[#d4cac4]",
                )}
              >
                <span
                  className={cx(
                    "absolute top-0.5 size-4 rounded-full bg-white shadow transition-[left]",
                    on ? "left-[18px]" : "left-0.5",
                  )}
                />
              </span>
            </label>
          );
        })}
      </div>
      {save.error && <ErrorAlert error={save.error} />}
    </Modal>
  );
}
